import React, { useEffect } from 'react';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';
import { LibraryImpact } from './types';
import { iconButton, secondaryButton } from '../ui';
import { ROUTES } from '../../../config/links';

type ImpactDialogProps = {
  impact: LibraryImpact;
  /** What confirming does, e.g. "Publish anyway". */
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/** Lists the published forms that would lose fields, and asks for an explicit go-ahead. */
export default function ImpactDialog({ impact, confirmLabel, busy, onConfirm, onCancel }: ImpactDialogProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" role="alertdialog" aria-modal="true" aria-labelledby="impact-title" aria-describedby="impact-description">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-4">
          <div className="flex items-start gap-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-600"><AlertTriangle className="h-4 w-4" /></span>
            <div>
              <h2 id="impact-title" className="text-base font-semibold text-slate-900">Published forms use fields you are removing</h2>
              <p id="impact-description" className="mt-0.5 text-[12px] text-slate-500">
                {impact.message} Users will keep the form but no longer see these fields. A calibration may be rejected or incomplete without them.
              </p>
            </div>
          </div>
          <button type="button" className={iconButton} onClick={onCancel} disabled={busy} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <div>
            <div className="text-[12px] font-medium text-slate-700">Removed entries</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {impact.removed_entries.map((key) => <code key={key} className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-700">{key}</code>)}
            </div>
          </div>
          <div>
            <div className="text-[12px] font-medium text-slate-700">Forms affected ({impact.forms.length})</div>
            <ul className="mt-1 divide-y divide-slate-100 rounded-lg border border-slate-200">
              {impact.forms.map((form) => (
                <li key={form.form_id} className="flex items-start justify-between gap-3 p-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-[12px] font-medium text-slate-800">{form.name} <span className="font-normal text-slate-400">· v{form.version}</span></div>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {form.entries.map((key) => <code key={key} className="rounded bg-amber-50 px-1 text-[10px] text-amber-700">{key}</code>)}
                    </div>
                  </div>
                  <a className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-primary-700 hover:underline" href={ROUTES.formBuilderEdit(form.form_id)} target="_blank" rel="noreferrer">
                    Open form <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <p className="rounded-lg bg-slate-50 p-2.5 text-[11px] text-slate-600">
            Each form can only be re-published after the removed entries are taken out of its selection. You can also roll this library back later from its version history.
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 p-4">
          <button type="button" className={secondaryButton} onClick={onCancel} disabled={busy} autoFocus>Cancel</button>
          <button type="button" className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-amber-600 px-3.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-amber-700 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50" onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
